// src/router/guards.js
import { Capacitor } from "@capacitor/core";
import store from "@/store";
import { cookieManager } from "@/service/cookieManager";
import { userRepository } from "@/service/repository/userRepository";
import { changeLocale, i18n } from "@/i18n";
import { faviconService } from "@/service/faviconService";

const loadUser = async (uid = null) => {
	if (uid) {
		await store.dispatch("user/find", uid);
	} else {
		const token = cookieManager.getToken();
		if (token) {
			await store.dispatch("user/loginWithToken", token);
		}
		return store.getters["user/user"];
	}
};

const initializeApp = async () => {
	await store.dispatch("config/showLoadingScreen");
	const websocketUrl = store.getters["config/websocketUrl"];
	const venueId = store.getters["venue/venueId"];

	if (!websocketUrl || !venueId) {
		await store.dispatch("config/loadSystemConfig");
		await store.dispatch("venue/loadVenueConfig");
		await store.dispatch("accounting/loadAccountingConfig");

		const logicalDate = store.getters["config/logicalDate"];
		await store.commit("copilot/setServerLogicalDate", logicalDate);
		await store.commit("copilot/setSelectedDate", { selectedDate: logicalDate });
	}

	let initialLocale = "es";
	let localeChange = false;
	const cookieSelectedLocale = cookieManager.getCookie("selectedLocale");
	if (cookieSelectedLocale) {
		localeChange = initialLocale !== cookieSelectedLocale;
		initialLocale = cookieSelectedLocale;
	}
	await changeLocale(i18n, "es");
	if (localeChange) {
		await changeLocale(i18n, initialLocale);
	}

	await store.dispatch("copilot/fetchStaffList");
};

export function setupGuards(router) {
	router.beforeEach(async (to, from, next) => {
		let user = null;
		let resultRoleCopilot = null;

		await initializeApp();

		if (to.name !== "Menu" && to.name !== "Reserve" && to.name !== "Home") {
			cookieManager.setConsent(true);
		}

		const uid = to.query.uid;

		faviconService.changeFaviconByRoute(to.path);
		document.title = `${to.meta.title} | ${store.getters["venue/businessName"]}` || "WeWelcom";

		switch (to.name) {
			case "Root":
				if (Capacitor.isNativePlatform()) {
					next("/copilot");
				} else {
					next("/reservar");
				}
				return;

			case "Menu":
				user = await loadUser(uid);
				await store.dispatch("menu/loadMenus");
				break;

			case "TakeAway":
				user = await loadUser(uid);
				await store.dispatch("menu/loadMenus");
				break;

			case "Reserve":
				store.commit("reservationForm/setIsIframe", { isIframe: false });
				await store.dispatch("reservationForm/setAvailableRooms");
				break;

			case "IframeReserve":
				store.commit("reservationForm/setIsIframe", { isIframe: true });
				await store.dispatch("reservationForm/setAvailableRooms");
				break;

			case "Copilot": {
				user = await loadUser(uid);
				resultRoleCopilot = await userRepository.checkRoleCopilot();
				if (!user || resultRoleCopilot === 401 || resultRoleCopilot === 403) {
					next("/login");
					return;
				}
				await store.dispatch("copilot/initAvailableRoles", { user });
				const selectedRoleId = cookieManager.getCookie("selectedRole");
				store.commit("copilot/setSelectedRoleId", { selectedRoleId });
				await store.dispatch("reservationForm/setAvailableRooms");
				await store.dispatch("copilot/getTables");
				await store.dispatch("menu/loadMenus");
				await store.dispatch("copilot/updateReservations");
				await store.dispatch("copilot/updateOrdersAndNotifications");
				// await store.dispatch("copilot/loginStaffFromCookie");
				break;
			}

			case "Ebitdata": {
				await store.dispatch("ebitdata/getTables");
				user = await loadUser(uid);
				resultRoleCopilot = await userRepository.checkRoleAdmin();
				if (!user || resultRoleCopilot === 401 || resultRoleCopilot === 403) {
					next("/login");
					return;
				}
				break;
			}

			case "Kiosk":
				user = await loadUser(uid);
				resultRoleCopilot = await userRepository.checkRoleCopilot();
				if (!user || resultRoleCopilot === 401 || resultRoleCopilot === 403) {
					next("/login");
					return;
				}
				break;

			case "Home":
				if (store.getters["venue/resourceId"] === "elcapirotedegranada") {
					faviconService.setFavicon(`${store.getters["config/weWelcomUrl"]}/world/img/${store.getters["venue/resourceId"]}/favicon/favicon.ico`);
				}
				await store.dispatch("reservationForm/setAvailableRooms");
				break;
		}

		next();
	});

	router.afterEach((to) => {
		const body = document.body;
		body.classList.remove("copilot", "kiosk", "login");

		if (to.path.startsWith("/copilot")) {
			body.classList.add("copilot");
		}
		if (to.path.startsWith("/kiosk")) {
			body.classList.add("kiosk");
		}
		if (to.path.startsWith("/login")) {
			body.classList.add("login");
		}

		store.dispatch("config/hideLoadingScreen");
	});
}
